/**
 * The whole map in one glance: every branch as a bar of steps, the current
 * step, the age-expected step, and a time slider that redraws the same bars
 * as they were months ago ("where was he, where is he").
 */

import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useParams } from '@tanstack/react-router';
import { Api } from '@/core/api';
import { fa, formatJalali } from '@/core/jalali';
import { STATUS_FA, type NodeState, type NodeStatus } from '@/core/types';
import { useProfile } from '@/app/ProfileShell';
import s from './map.module.css';

const COLOR: Record<NodeStatus, string> = {
  achieved: 'var(--st-achieved)',
  reported: 'var(--st-reported)',
  emerging: 'var(--st-emerging)',
  not_yet: 'var(--st-notyet)',
  unknown: 'var(--st-unknown-bg)',
  not_due: 'var(--st-notdue-bg)',
  lost: 'var(--st-lost)',
};

export function MapPage() {
  const { id } = useParams({ from: '/authed/p/$id' });
  const profile = useProfile();
  // Slider: 0 = now, N = N months ago (up to 36).
  const [monthsAgo, setMonthsAgo] = useState(0);
  const asOf = useMemo(() => {
    if (monthsAgo === 0) return undefined;
    const d = new Date();
    d.setMonth(d.getMonth() - monthsAgo);
    return d.toISOString();
  }, [monthsAgo]);

  const now = useQuery({ queryKey: ['state', id, 'now'], queryFn: () => Api.state(id) });
  const then = useQuery({
    queryKey: ['state', id, asOf],
    queryFn: () => Api.state(id, { asOf }),
    enabled: asOf !== undefined,
  });

  const byBranch = (nodes: NodeState[] | undefined) => {
    const m = new Map<string, NodeState[]>();
    for (const n of nodes ?? []) {
      if (n.kind === 'profile') continue;
      (m.get(n.branch) ?? m.set(n.branch, []).get(n.branch)!).push(n);
    }
    for (const list of m.values()) list.sort((a, b) => a.sequence - b.sequence);
    return m;
  };
  const nowMap = byBranch(now.data?.nodes);
  const thenMap = byBranch(then.data?.nodes);

  return (
    <main className="page">
      <h2 className="page-title">نقشهٔ مسیر رشد</h2>
      <section className="card">
        <div className="row spread">
          <span className="overline">{monthsAgo === 0 ? 'الان' : `${fa(monthsAgo)} ماه پیش`}</span>
          {asOf && <span className="help">{formatJalali(asOf)}</span>}
        </div>
        <input
          type="range"
          min={0}
          max={36}
          value={monthsAgo}
          onChange={(e) => setMonthsAgo(Number(e.target.value))}
          aria-label="زمان"
          className={s.slider}
          dir="ltr"
          style={{ transform: 'scaleX(-1)' }}
        />
        <div className="help">
          لغزنده را بکشید تا ببینید همین نقشه چند ماه پیش چه شکلی بود. رنگ کم‌رنگ = آن زمان.
        </div>
      </section>

      <section className="card" style={{ gap: 6 }}>
        <div className="row" style={{ gap: 6 }}>
          {(['achieved', 'reported', 'emerging', 'not_yet', 'not_due'] as NodeStatus[]).map(
            (st) => (
              <span key={st} className={`chip ${st}`}>
                {STATUS_FA[st]}
              </span>
            ),
          )}
        </div>
      </section>

      {now.isLoading && <div className="skeleton" />}
      {profile.branches.map((b) => {
        const steps = nowMap.get(b.branch) ?? [];
        const old = thenMap.get(b.branch) ?? [];
        const achievedNow = steps.filter((n) => n.status === 'achieved').length;
        return (
          <Link
            key={b.branch}
            to="/p/$id/path/$branch"
            params={{ id, branch: b.branch }}
            className={`card ${s.branch}`}
            style={{ color: 'inherit' }}
          >
            <div className="row spread">
              <strong>{b.title}</strong>
              <span className="help num">
                {fa(achievedNow)} از {fa(steps.length)}
              </span>
            </div>
            <div className={s.track} aria-hidden="true">
              {steps.map((n) => {
                const oldN = old.find((o) => o.key === n.key);
                return (
                  <span
                    key={n.key}
                    className={s.step}
                    style={{ background: COLOR[n.status] }}
                    data-current={b.currentNode === n.key || undefined}
                    data-expected={b.expectedNode === n.key || undefined}
                  >
                    {asOf && oldN && (
                      <span className={s.old} style={{ background: COLOR[oldN.status] }} />
                    )}
                  </span>
                );
              })}
            </div>
            <div className="help">
              {b.nextTitle ? `قدم بعدی: ${b.nextTitle}` : 'این شاخه تمام شده'}
              {b.ageGap !== null && b.ageGap > 1
                ? ` · ${fa(b.ageGap)} قدم تا جایی که برای این سن انتظار می‌رود`
                : ''}
            </div>
          </Link>
        );
      })}
    </main>
  );
}
